import amaLog, { amaWarn } from '../helpers/log';
import { isDesktop } from '../config/device';

type DeferredAdRunner = () => void;

class DeferredLoadingManager {
	private readonly name: string;
	private readonly batchViewportDistance: number = isDesktop() ? 3 : 4;
	private readonly observerRootMargin: string = `${this.batchViewportDistance * 110}% 0px`; // 10% overlap to catch fast scroll jumps.
	private readonly batchSize: number = 5;
	private readonly flushIntervalMs: number = 25;

	private deferredAds: DeferredAdRunner[] = [];
	private deferredAdsTimer: number | null = null;
	private deferredAdsObserver: IntersectionObserver | null = null;
	private deferredObservedAds: Map<Element, DeferredAdRunner> = new Map();

	constructor(name: string = 'Default') {
		this.name = name;
	}

	public reset(): void {
		if (this.deferredAdsTimer !== null) {
			clearTimeout(this.deferredAdsTimer);
			this.deferredAdsTimer = null;
		}

		this.deferredAds = [];
		this.deferredAdsObserver?.disconnect();
		this.deferredAdsObserver = null;
		this.deferredObservedAds.clear();
	}

	public schedule(runDeferredAd: DeferredAdRunner, div?: HTMLElement): void {
		if (this.shouldBatchDeferredAd(div)) {
			amaLog(`DEFER[${this.name}] - Scheduling for batch processing.`, div?.id);
			this.enqueueDeferredAd(runDeferredAd, div);
			return;
		}

		const observedRunner = this.createDeferredRunner(runDeferredAd, div);
		amaLog(`DEFER[${this.name}] - Scheduling for one-by-one observation loading.`, { divId: div?.id });
		this.observeDeferredAd(div, observedRunner);
	}

	private shouldBatchDeferredAd(div?: HTMLElement): boolean {
		if (!div) return true;

		const rect = div.getBoundingClientRect();
		const windowHeight = window.innerHeight || document.documentElement.clientHeight;
		const belowFoldDistance = rect.top - windowHeight;
		return belowFoldDistance <= windowHeight * this.batchViewportDistance;
	}

	private createDeferredRunner(fn: DeferredAdRunner, div?: HTMLElement): DeferredAdRunner {
		let pending = true;

		return () => {
			if (!pending) return;
			pending = false;
			this.cleanupDeferredObservation(div);
			fn();
		};
	}

	private enqueueDeferredAd(fn: DeferredAdRunner, div?: HTMLElement): DeferredAdRunner {
		const runner = this.createDeferredRunner(fn, div);

		amaLog(`DEFER[${this.name}] - Enqueuing deferred ad ${div?.id} Batch size: ${this.deferredAds.length + 1}.`);

		this.deferredAds.push(runner);

		if (this.deferredAdsTimer === null) {
			this.deferredAdsTimer = window.setTimeout(() => {
				this.flushDeferredAds();
			}, this.flushIntervalMs);
		}

		return runner;
	}

	private observeDeferredAd(div: HTMLElement | undefined, deferredRunner: DeferredAdRunner): void {
		if (typeof deferredRunner !== 'function') {
			amaWarn(`DEFER[${this.name}] - Invalid deferredRunner provided, expected function.`, {
				divId: div?.id,
				deferredRunner: deferredRunner,
			});
			return;
		}
		if (!div || typeof window.IntersectionObserver === 'undefined') {
			amaLog(`DEFER[${this.name}] - IntersectionObserver unavailable, or div missing. Running ad immediately.`, { divId: div?.id });
			deferredRunner();
			return;
		}

		if (!this.deferredAdsObserver) {
			this.deferredAdsObserver = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						if (!entry.isIntersecting) return;

						const runner = this.deferredObservedAds.get(entry.target);
						if (!runner) return;

						this.cleanupDeferredObservation(entry.target);

						amaLog(`DEFER[${this.name}] - Observer triggered for deferred ad`, { divId: entry.target.id });
						runner();
					});
				},
				{
					root: null,
					rootMargin: this.observerRootMargin,
					threshold: 0.01,
				},
			);
		}

		this.deferredObservedAds.set(div, deferredRunner);
		this.deferredAdsObserver.observe(div);
	}

	private cleanupDeferredObservation(element?: Element): void {
		if (!element) return;

		this.deferredAdsObserver?.unobserve(element);
		this.deferredObservedAds.delete(element);
	}

	public flushDeferredAds(): void {
		if (this.deferredAdsTimer !== null) {
			clearTimeout(this.deferredAdsTimer);
			this.deferredAdsTimer = null;
		}

		if (!this.deferredAds.length) {
			return;
		}

		const deferredAdsToRun = this.deferredAds.splice(0, this.batchSize);

		amaLog(`DEFER[${this.name}] - Processing deferred ads`, { deferredAds: deferredAdsToRun.length });

		deferredAdsToRun.forEach((deferredAd) => deferredAd());

		if (this.deferredAds.length) {
			this.deferredAdsTimer = window.setTimeout(() => {
				this.flushDeferredAds();
			}, this.flushIntervalMs);
		}
	}
}

export const deferredGptLoadingManager = new DeferredLoadingManager('GPT');

// Dedicated queue for first-wave non-lazy batching to avoid mixing with deferred placement scheduling.
export const deferredFirstWaveBatchManager = new DeferredLoadingManager('FirstWaveBatch');
